<script setup>
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useBranding } from 'shared/composables/useBranding';
import { picoSearch } from '@scmmishra/pico-search';
import IntegrationItem from './IntegrationItem.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { replaceInstallationName } = useBranding();

const searchQuery = ref('');
const uiFlags = getters['integrations/getUIFlags'];

// Providers tagged with a shared `category` (calendar, email, ...) are
// grouped under one dedicated entry each (below) instead of appearing
// individually here, so connecting one doesn't require knowing which
// specific provider card to look for. New categories only need an entry
// added to GROUPED_CATEGORIES — no other change here.
const GROUPED_CATEGORIES = [
  {
    category: 'calendar',
    id: 'calendar',
    icon: 'i-lucide-calendar-days',
    nameKey: 'CALENDAR_INTEGRATIONS.TITLE',
    descriptionKey: 'CALENDAR_INTEGRATIONS.DESCRIPTION',
  },
  {
    category: 'email',
    id: 'email',
    icon: 'i-lucide-mail',
    nameKey: 'EMAIL_INTEGRATIONS.TITLE',
    descriptionKey: 'EMAIL_INTEGRATIONS.DESCRIPTION',
  },
];

const groupedCategoryIds = GROUPED_CATEGORIES.map(group => group.category);

const groupTiles = computed(() => {
  const all = getters['integrations/getAppIntegrations'].value;
  return GROUPED_CATEGORIES.filter(group =>
    all.some(item => item.category === group.category)
  ).map(group => ({
    id: group.id,
    icon: group.icon,
    name: t(group.nameKey),
    description: t(group.descriptionKey),
    enabled: all.some(item => item.category === group.category && item.enabled),
  }));
});

const integrationList = computed(() => {
  const ungrouped = getters['integrations/getAppIntegrations'].value.filter(
    item => !groupedCategoryIds.includes(item.category)
  );
  return [...ungrouped, ...groupTiles.value];
});

const filteredIntegrationList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return integrationList.value;
  return picoSearch(integrationList.value, query, ['name', 'description']);
});

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('INTEGRATION_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INTEGRATION_SETTINGS.HEADER')"
        :description="
          replaceInstallationName($t('INTEGRATION_SETTINGS.DESCRIPTION'))
        "
        :link-text="$t('INTEGRATION_SETTINGS.LEARN_MORE')"
        :search-placeholder="$t('INTEGRATION_SETTINGS.SEARCH_PLACEHOLDER')"
        feature-name="integrations"
      />
    </template>
    <template #body>
      <div class="flex-grow flex-shrink overflow-auto">
        <span
          v-if="!filteredIntegrationList.length && searchQuery"
          class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.NO_RESULTS') }}
        </span>
        <div
          v-else
          class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
        >
          <IntegrationItem
            v-for="item in filteredIntegrationList"
            :id="item.id"
            :key="item.id"
            :icon="item.icon"
            :name="item.name"
            :description="item.description"
            :enabled="item.enabled"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
